import type {
    MutationScope,
    QueryClient,
    QueryKey,
    UseMutationOptions,
    UseMutationResult,
    UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { SerializedUiSettings, UserUiSettings } from '~/shared/data-contract/user-ui-settings';
import { uiSettingsSchema } from '~/shared/data-contract/user-ui-settings';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

const pathname = `/users/me/settings/ui`;
const queryKey: QueryKey = [pathname, 'get'];

// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
const serializeSetting = <K extends keyof UserUiSettings>(value: any | null): SerializedUiSettings[K] =>
    value === null ? null : typeof value === 'object' ? JSON.stringify(value) : value;

const mapObject = <V, M, R extends Record<string, V>>(object: R, mapper: (value: V) => M): Record<string, M> =>
    Object.fromEntries(Object.entries(object).map(([key, value]) => [key, mapper(value)]));

const serializeSettings = (settings: UserUiSettings): SerializedUiSettings => mapObject(settings, serializeSetting);

export const readUserSettingsUi = (queryClient: QueryClient): UserUiSettings | undefined =>
    queryClient.getQueryData<UserUiSettings>(queryKey);

export const useUserSettingsUi = (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
    options: { readonly enabled: boolean },
): UseQueryResult<UserUiSettings> =>
    useQuery({
        enabled: options.enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(uiSettingsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

type UserSettingsMutationContext = {
    readonly previousSettings: UserUiSettings | undefined;
};

export const useUserSettingsUiMutation = (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
    scope?: MutationScope,
): {
    readonly userSettingsUiMutation: UseMutationResult<
        UserUiSettings,
        Error,
        Partial<UserUiSettings>,
        UserSettingsMutationContext
    >;
    readonly userSettingsUiMutationOptimistic: UseMutationResult<
        UserUiSettings,
        Error,
        Partial<UserUiSettings>,
        UserSettingsMutationContext
    >;
} => {
    const queryClient = useQueryClient();

    const hasQueuedWrite = (): boolean =>
        scope !== undefined &&
        queryClient.isMutating({ predicate: (mutation) => mutation.options.scope?.id === scope.id }) > 1;

    const restorePreviousSettings = (context: UserSettingsMutationContext | undefined): void => {
        if (!hasQueuedWrite()) {
            queryClient.setQueryData(queryKey, context?.previousSettings);
        }
    };

    const optimisticMutation: UseMutationOptions<
        UserUiSettings,
        Error,
        Partial<UserUiSettings>,
        UserSettingsMutationContext
    > = {
        scope,
        mutationFn: async (data) => {
            const result = await validatingCobrainerApiFetcher.patchJson(uiSettingsSchema, {
                pathname,
                payload: serializeSettings(data),
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onMutate: async (newSettings) => {
            await queryClient.cancelQueries({ queryKey });
            const previousSettings = queryClient.getQueryData<UserUiSettings>(queryKey);
            queryClient.setQueryData<UserUiSettings>(queryKey, (oldSettings) => ({
                ...oldSettings,
                ...newSettings,
            }));

            return { previousSettings };
        },
        onError: async (_error, _newSettings, context) => {
            restorePreviousSettings(context);

            if (scope !== undefined && !hasQueuedWrite()) {
                await queryClient.invalidateQueries({ queryKey });
            }
        },
        onSuccess: (settings) => {
            if (!hasQueuedWrite()) {
                queryClient.setQueryData(queryKey, settings);
            }
        },
    };

    const userSettingsUiMutation = useMutation({
        ...optimisticMutation,
        onError: (_error, _newSettings, context) => {
            restorePreviousSettings(context);

            return queryClient.invalidateQueries({ queryKey });
        },
    });

    const userSettingsUiMutationOptimistic = useMutation(optimisticMutation);

    return { userSettingsUiMutation, userSettingsUiMutationOptimistic };
};
